import {DOCUMENT} from '@angular/common';
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {LinkDefinition} from '../types/LinkDefinition';
import {ToolsService} from './tools.service';
import {UrlTermsSymbolsIgnoredInCanonicalsEnum} from '../enums/SeoEnums';
import {AppConfigService} from './app-config.service';
import {RoutesEnums} from '../enums/RoutesEnums';

@Injectable()
export class LinkService implements OnDestroy {
  private routeListener: Subscription;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private tools: ToolsService,
    private appConfigService: AppConfigService,
  ) {}

  public updateTag(tag: LinkDefinition): void {
    const selector = this._parseSelector(tag);
    const linkElement = this.document.head.querySelector(selector) as HTMLLinkElement;

    if (linkElement) {
      Object.keys(tag).forEach((prop: string) => {
        linkElement.setAttribute(prop, tag[prop]);
      });
    } else {
      const linkElementToAdd = this.document.createElement('link');
      Object.keys(tag).forEach((prop: string) => {
        linkElementToAdd.setAttribute(prop, tag[prop]);
      });
      this.document.head.appendChild(linkElementToAdd);
    }
  }

  public removeTag(tag: LinkDefinition): void {
    const selector = this._parseSelector(tag);
    const linkElement = this.document.head.querySelector(selector) as HTMLLinkElement;

    if (linkElement) {
      this.document.head.removeChild(linkElement);
    }
  }

  public getTag(tag: LinkDefinition): HTMLLinkElement {
    const selector = this._parseSelector(tag);

    return this.document.head.querySelector(selector);
  }

  public startRouteListener(): void {
    this.routeListener = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.handleRouteChange());
  }

  ngOnDestroy(): void {
    this.routeListener.unsubscribe();
  }

  private handleRouteChange(): void {
    let url = '';
    let queryParamsStr = '';
    let isItSerp: boolean = false;

    const urlTree = this.router.parseUrl(this.router.url);
    if (urlTree.root.hasChildren()) {
      const childrenKey = 'primary';
      const segments = urlTree.root.children[childrenKey].segments;
      if (segments && segments.length > 0) {
        url = segments.map((segment) => segment.path).join('/');
        isItSerp = segments[0].path === RoutesEnums.SEARCH;
      }
    }
    if (!this.tools.isNil(urlTree.queryParams)) {
      const queryStringCombinedArr: string[] = [];
      Object.keys(urlTree.queryParams)
        .filter((item) => !(<any>Object).values(UrlTermsSymbolsIgnoredInCanonicalsEnum).includes(item))
        .forEach((key) => {
          queryStringCombinedArr.push(`${key}=${urlTree.queryParams[key]}`);
        });
      if (queryStringCombinedArr.length > 0) {
        queryParamsStr += `?${queryStringCombinedArr.join('&')}`;
      }
    }

    this.removeTag({rel: 'next'});
    this.removeTag({rel: 'prev'});
    if (!isItSerp) {
      // skip serp pages since canonicals are base on search state there
      this.removeTag({rel: 'canonical'});
      this.updateTag({
        rel: 'canonical',
        href: `${this.appConfigService.appConfig.baseUrl}${url}${queryParamsStr}`,
      });
    }
  }

  private _parseSelector(tag: LinkDefinition): string {
    let attr: string;
    if (tag.rel) {
      attr = 'rel';
    } else if (tag.hreflang) {
      attr = 'hreflang';
    } else if (tag.href) {
      attr = 'href';
    }
    return `link[${attr}="${tag[attr]}"]`;
  }
}
